import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { generatePayrollRunPdf } from "@/utils/reports.functions";
import {
  sendPayrollNotifications,
  getPayrollRunEmailTimeline,
  type PayrollEmailEvent,
} from "@/utils/payroll-email.functions";
import { downloadPdfFromBase64 } from "@/lib/report-export";
import { buildWhatsAppUrl } from "@/lib/phone";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PdfCanvasPreview } from "@/components/settings/PdfCanvasPreview";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Download,
  FileText,
  CheckCircle2,
  CircleDollarSign,
  Trash2,
  Ban,
  Send,
  Mail,
  XCircle,
  Clock,
  RefreshCw,
  Loader2,
  FileDown,
  AlertCircle,
  Eye,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/payroll/$runId")({
  component: PayrollRunDetail,
});

type RunStatus = "draft" | "pending_employee_approval" | "finalized" | "paid" | "cancelled";

type Run = {
  id: string;
  number: string;
  name: string | null;
  period_start: string;
  period_end: string;
  pay_date: string | null;
  status: RunStatus;
  currency: string;
  total_hours: number;
  total_gross: number;
  notes: string | null;
  finalized_at: string | null;
  created_at: string;
};

type Item = {
  id: string;
  user_id: string;
  employee_name: string | null;
  employee_email: string | null;
  employee_phone?: string | null;
  hours: number;
  hourly_rate: number;
  pay_type: string;
  gross_pay: number;
  entries_count: number;
  employee_approved_at: string | null;
  employee_rejected_at: string | null;
  employee_rejection_reason: string | null;
};

function PayrollRunDetail() {
  const { t } = useTranslation();
  const { runId } = Route.useParams();
  const { company, hasRole } = useAuth();
  const navigate = useNavigate();
  const isAdmin = hasRole("admin");

  const [run, setRun] = useState<Run | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewBase64, setPdfPreviewBase64] = useState<string | null>(null);
  const [pdfPreviewFilename, setPdfPreviewFilename] = useState<string>("");
  const [sendingEmails, setSendingEmails] = useState(false);
  const pdfFn = useServerFn(generatePayrollRunPdf);
  const sendEmailsFn = useServerFn(sendPayrollNotifications);
  const timelineFn = useServerFn(getPayrollRunEmailTimeline);
  const [emailEventsByItem, setEmailEventsByItem] = useState<Record<string, PayrollEmailEvent[]>>(
    {},
  );
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  const loadTimeline = async () => {
    setTimelineLoading(true);
    try {
      const res = await timelineFn({ data: { runId } });
      const map: Record<string, PayrollEmailEvent[]> = {};
      for (const t of res.timelines) {
        map[t.payroll_run_item_id] = t.events;
      }
      setEmailEventsByItem(map);
    } catch {
      // silent — timeline is supplemental
    } finally {
      setTimelineLoading(false);
    }
  };

  const openPdfPreview = async () => {
    if (!run) return;
    setExportingPdf(true);
    try {
      const { pdfBase64, filename } = await pdfFn({ data: { runId: run.id } });
      setPdfPreviewBase64(pdfBase64);
      setPdfPreviewFilename(filename || `folha-${run.number}.pdf`);
      setPdfPreviewOpen(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t("pages.payroll.detail.pdfFailedDefault");
      toast.error(t("pages.payroll.detail.pdfFailedTitle"), { description: msg });
    } finally {
      setExportingPdf(false);
    }
  };

  const confirmDownloadPdf = () => {
    if (!pdfPreviewBase64) return;
    downloadPdfFromBase64(
      pdfPreviewFilename || `folha-${run?.number ?? "payroll"}.pdf`,
      pdfPreviewBase64,
    );
    toast.success(t("pages.payroll.detail.pdfDownloaded"));
  };

  const closePdfPreview = () => {
    setPdfPreviewOpen(false);
    setPdfPreviewBase64(null);
  };

  const load = async () => {
    setLoading(true);
    const [{ data: r, error: re }, { data: its, error: ie }] = await Promise.all([
      supabase.from("payroll_runs").select("*").eq("id", runId).maybeSingle(),
      supabase
        .from("payroll_run_items")
        .select("*")
        .eq("payroll_run_id", runId)
        .order("employee_name"),
    ]);
    if (re) toast.error(re.message);
    if (ie) toast.error(ie.message);
    setRun((r as Run) ?? null);
    const baseItems = (its ?? []) as Item[];
    // Fetch employee phone numbers from profiles in one shot.
    const userIds = Array.from(new Set(baseItems.map((i) => i.user_id))).filter(Boolean);
    let phoneByUser: Record<string, string | null> = {};
    if (userIds.length > 0) {
      const { data: profs } = await supabase.from("profiles").select("id, phone").in("id", userIds);
      phoneByUser = Object.fromEntries((profs ?? []).map((p) => [p.id, p.phone ?? null]));
    }
    setItems(baseItems.map((i) => ({ ...i, employee_phone: phoneByUser[i.user_id] ?? null })));
    setLoading(false);
  };

  useEffect(() => {
    load();
    loadTimeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  const fmtMoney = (v: number) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: run?.currency ?? company?.currency ?? "USD",
    }).format(v);

  const sendNotifications = async (runId: string, opts?: { silent?: boolean }) => {
    setSendingEmails(true);
    try {
      const res = await sendEmailsFn({
        data: { runId, appOrigin: window.location.origin },
      });
      if (res.sent > 0) {
        toast.success(
          t("pages.payroll.detail.emailsSent", {
            count: res.sent,
            defaultValue: "Sent {{count}} email(s) to employees",
          }),
        );
      } else if (!opts?.silent) {
        toast.info(
          t("pages.payroll.detail.emailsNoneSent", {
            defaultValue: "No emails were sent (no valid employee email addresses).",
          }),
        );
      }
      if (res.failures.length > 0) {
        toast.error(
          t("pages.payroll.detail.emailsFailed", {
            defaultValue: "Some emails failed to queue",
          }),
          { description: res.failures.slice(0, 3).join("\n") },
        );
      }
    } catch (e) {
      toast.error(
        e instanceof Error
          ? e.message
          : t("pages.payroll.detail.emailsFailed", {
              defaultValue: "Failed to send emails",
            }),
      );
    } finally {
      setSendingEmails(false);
      void loadTimeline();
    }
  };

  const setStatus = async (status: Run["status"]) => {
    if (!run) return;
    const patch: Partial<Run> = { status };
    if (status === "finalized") patch.finalized_at = new Date().toISOString();
    if (status === "paid" && !run.pay_date) {
      patch.pay_date = new Date().toISOString().slice(0, 10);
    }
    const { error } = await supabase.from("payroll_runs").update(patch).eq("id", run.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t("pages.payroll.detail.markedAs", { status: t(`statuses.${status}`) }));
    await load();
  };

  const sendWhatsApp = async (item: Item) => {
    if (!run) return;
    const phone = item.employee_phone?.trim();
    if (!phone) {
      toast.error(
        t("pages.payroll.detail.waNoPhone", {
          defaultValue: "This employee has no phone number on file.",
        }),
      );
      return;
    }
    const confirmUrl = `${window.location.origin}/payroll/confirm/${item.id}`;
    const startStr = new Date(run.period_start).toLocaleDateString(undefined, { timeZone: "UTC" });
    const endStr = new Date(run.period_end).toLocaleDateString(undefined, { timeZone: "UTC" });
    const periodStr = `${startStr} – ${endStr}`;
    const greeting = item.employee_name
      ? t("pages.payroll.detail.waMessageGreeting", {
          name: item.employee_name,
          defaultValue: "Hi {{name}},",
        })
      : t("pages.payroll.detail.waMessageGreetingFallback", {
          defaultValue: "Hi,",
        });
    const message = t("pages.payroll.detail.waMessageBody", {
      greeting,
      number: run.number,
      period: periodStr,
      gross: fmtMoney(Number(item.gross_pay)),
      hours: Number(item.hours).toFixed(2),
      url: confirmUrl,
      defaultValue:
        "{{greeting}}\n\nYour paystub for {{number}} ({{period}}) is ready.\n• Hours: {{hours}}\n• Gross pay: {{gross}}\n\nPlease confirm receipt here:\n{{url}}",
    });
    const result = buildWhatsAppUrl(phone, message);
    if ("error" in result) {
      toast.error(
        t("pages.payroll.detail.waInvalidPhone", {
          defaultValue: "Invalid phone number",
        }),
        { description: result.error },
      );
      return;
    }
    window.open(result.url, "_blank", "noopener,noreferrer");
  };

  const handleDelete = async () => {
    if (!run) return;
    const { error } = await supabase.from("payroll_runs").delete().eq("id", run.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t("pages.payroll.detail.deleted"));
    navigate({ to: "/payroll" });
  };

  const downloadCSV = () => {
    if (!run) return;
    const header = [
      t("pages.payroll.detail.tableEmployee"),
      t("common.email"),
      t("pages.payroll.detail.tableEntries"),
      t("pages.payroll.detail.tableHours"),
      t("pages.payroll.detail.tableRate"),
      t("pages.payroll.detail.tableGross"),
      t("common.currency", { defaultValue: "Currency" }),
    ];
    const rows = items.map((i) => [
      i.employee_name ?? "",
      i.employee_email ?? "",
      String(i.entries_count),
      Number(i.hours).toFixed(2),
      Number(i.hourly_rate).toFixed(2),
      Number(i.gross_pay).toFixed(2),
      run.currency,
    ]);
    const totals = [
      t("pages.payroll.detail.totalRow").toUpperCase(),
      "",
      String(items.reduce((s, i) => s + i.entries_count, 0)),
      Number(run.total_hours).toFixed(2),
      "",
      Number(run.total_gross).toFixed(2),
      run.currency,
    ];
    const escape = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
    const csv = [header, ...rows, totals].map((r) => r.map(escape).join(",")).join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${run.number}_${run.period_start}_${run.period_end}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">
        {t("pages.payroll.detail.loading")}
      </p>
    );
  }
  if (!run) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-muted-foreground">{t("pages.payroll.detail.notFound")}</p>
        <Link to="/payroll" className="mt-4 inline-block text-sm text-primary hover:underline">
          {t("pages.payroll.detail.backLink")}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/payroll"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" /> {t("pages.payroll.detail.back")}
        </Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">{run.number}</h1>
              <StatusBadge status={run.status} />
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {run.name ?? t("pages.payroll.detail.payPeriod")} ·{" "}
              {new Date(run.period_start).toLocaleDateString(undefined, { timeZone: "UTC" })} –{" "}
              {new Date(run.period_end).toLocaleDateString(undefined, { timeZone: "UTC" })}
              {run.pay_date && (
                <>
                  {" · "}
                  {t("pages.payroll.detail.paysOn", {
                    date: new Date(run.pay_date).toLocaleDateString(),
                  })}
                </>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="gap-2" onClick={downloadCSV}>
              <Download className="h-4 w-4" /> {t("pages.payroll.detail.downloadCsv")}
            </Button>
            <Button
              variant="outline"
              className="gap-2"
              onClick={openPdfPreview}
              disabled={exportingPdf}
            >
              {exportingPdf ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <FileText className="h-4 w-4" />
              )}
              {t("pages.payroll.detail.previewPdf")}
            </Button>
            {isAdmin && run.status === "draft" && items.length > 0 && (
              <Button className="gap-2" onClick={() => setStatus("finalized")}>
                <CheckCircle2 className="h-4 w-4" /> Aprovar folha
              </Button>
            )}
            {isAdmin && run.status === "pending_employee_approval" && (
              <Button
                className="gap-2"
                onClick={() => setStatus("finalized")}
                title={t("pages.payroll.detail.unlockFinalizeTooltip")}
              >
                <CheckCircle2 className="h-4 w-4" /> Finalizar folha
              </Button>
            )}
            {isAdmin && (run.status === "finalized" || run.status === "paid") && (
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => sendNotifications(run.id)}
                disabled={sendingEmails}
              >
                {sendingEmails ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4" />
                )}
                Notificar funcionários
              </Button>
            )}
            {isAdmin && run.status === "finalized" && (
              <Button className="gap-2" onClick={() => setStatus("paid")}>
                <CircleDollarSign className="h-4 w-4" /> {t("pages.payroll.detail.markPaid")}
              </Button>
            )}
            {isAdmin && run.status !== "cancelled" && run.status !== "paid" && (
              <Button variant="outline" className="gap-2" onClick={() => setStatus("cancelled")}>
                <Ban className="h-4 w-4" /> {t("pages.payroll.detail.cancel")}
              </Button>
            )}
            {isAdmin && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="gap-2 text-destructive">
                    <Trash2 className="h-4 w-4" /> {t("pages.payroll.detail.delete")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("pages.payroll.detail.deleteTitle")}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("pages.payroll.detail.deleteDesc")}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("pages.payroll.detail.cancel")}</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete}>
                      {t("pages.payroll.detail.delete")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">{t("pages.payroll.detail.employees")}</p>
            <p className="mt-1 text-2xl font-bold">{items.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">{t("pages.payroll.detail.totalHours")}</p>
            <p className="mt-1 text-2xl font-bold">{Number(run.total_hours).toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">{t("pages.payroll.detail.totalGross")}</p>
            <p className="mt-1 text-2xl font-bold">{fmtMoney(Number(run.total_gross))}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("pages.payroll.detail.earningsByEmployee")}</CardTitle>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {t("pages.payroll.detail.noLineItems")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="w-8 py-2" />
                    <th className="py-2 pr-2">{t("pages.payroll.detail.tableEmployee")}</th>
                    <th className="py-2 pr-2 text-right">
                      {t("pages.payroll.detail.tableEntries")}
                    </th>
                    <th className="py-2 pr-2 text-right">{t("pages.payroll.detail.tableHours")}</th>
                    <th className="py-2 pr-2 text-right">{t("pages.payroll.detail.tableRate")}</th>
                    <th className="py-2 pr-2 text-right">{t("pages.payroll.detail.tableGross")}</th>
                    <th className="w-10 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {items.map((i) => {
                    const events = emailEventsByItem[i.id] ?? [];
                    const isExpanded = expandedItemId === i.id;
                    return (
                      <Fragment key={i.id}>
                        <tr>
                          <td className="py-2 pl-1 align-middle">
                            <button
                              type="button"
                              onClick={() => setExpandedItemId(isExpanded ? null : i.id)}
                              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                              aria-label={
                                isExpanded
                                  ? t("pages.payroll.detail.timelineCollapse", {
                                      defaultValue: "Collapse timeline",
                                    })
                                  : t("pages.payroll.detail.timelineExpand", {
                                      defaultValue: "Expand timeline",
                                    })
                              }
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                          </td>
                          <td className="py-2 pr-2">
                            <p className="font-medium">{i.employee_name ?? "—"}</p>
                            <p className="text-xs text-muted-foreground">
                              {i.employee_email ?? ""}
                            </p>
                          </td>
                          <td className="py-2 pr-2 text-right tabular-nums">{i.entries_count}</td>
                          <td className="py-2 pr-2 text-right tabular-nums">
                            {Number(i.hours).toFixed(2)}
                          </td>
                          <td className="py-2 pr-2 text-right tabular-nums">
                            {fmtMoney(Number(i.hourly_rate))}
                            <span className="ml-1 text-xs text-muted-foreground">
                              {i.pay_type === "daily"
                                ? t("pages.team.payType.dailyShort")
                                : t("pages.team.payType.hourlyShort")}
                            </span>
                          </td>

                          <td className="py-2 pr-2 text-right font-semibold tabular-nums">
                            {fmtMoney(Number(i.gross_pay))}
                          </td>
                          <td className="py-2 pr-1 text-right">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-primary hover:bg-primary/10 hover:text-primary"
                              onClick={() => sendWhatsApp(i)}
                              disabled={!i.employee_phone}
                              title={
                                i.employee_phone
                                  ? t("pages.payroll.detail.sendViaWhatsApp", {
                                      defaultValue: "Send paystub link via WhatsApp",
                                    })
                                  : t("pages.payroll.detail.waNoPhone", {
                                      defaultValue: "This employee has no phone number on file.",
                                    })
                              }
                            >
                              <MessageCircle className="h-4 w-4" />
                              <span className="sr-only">
                                {t("pages.payroll.detail.sendViaWhatsApp", {
                                  defaultValue: "Send paystub link via WhatsApp",
                                })}
                              </span>
                            </Button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="bg-muted/30">
                            <td />
                            <td colSpan={6} className="py-3 pr-2">
                              <EmailTimeline events={events} loading={timelineLoading} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot className="border-t border-border font-semibold">
                  <tr>
                    <td />
                    <td className="py-2 pr-2">{t("pages.payroll.detail.totalRow")}</td>
                    <td />
                    <td className="py-2 pr-2 text-right tabular-nums">
                      {Number(run.total_hours).toFixed(2)}
                    </td>
                    <td />
                    <td className="py-2 pr-2 text-right tabular-nums">
                      {fmtMoney(Number(run.total_gross))}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {run.notes && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t("pages.payroll.detail.notes")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-sm">{run.notes}</p>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={pdfPreviewOpen}
        onOpenChange={(open) => (open ? setPdfPreviewOpen(true) : closePdfPreview())}
      >
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>{t("pages.payroll.detail.pdfPreviewTitle")}</DialogTitle>
            <DialogDescription>
              {t("pages.payroll.detail.pdfPreviewDesc", { filename: pdfPreviewFilename })}
            </DialogDescription>
          </DialogHeader>
          <div className="h-[70vh] w-full overflow-hidden rounded-md border border-border bg-muted">
            {pdfPreviewBase64 ? (
              <PdfCanvasPreview pdfBase64={pdfPreviewBase64} />
            ) : (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closePdfPreview}>
              {t("pages.payroll.detail.pdfClose")}
            </Button>
            <Button onClick={confirmDownloadPdf} disabled={!pdfPreviewBase64} className="gap-2">
              <FileDown className="h-4 w-4" />
              {t("pages.payroll.detail.pdfDownload")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusBadge({ status }: { status: RunStatus }) {
  const { t } = useTranslation();
  const map: Record<RunStatus, string> = {
    draft: "border-muted-foreground/40 text-muted-foreground",
    pending_employee_approval: "border-primary/40 text-primary",
    finalized: "border-primary/40 text-primary",
    paid: "border-primary/40 bg-primary/10 text-primary",
    cancelled: "border-destructive/40 text-destructive",
  };
  return (
    <Badge variant="outline" className={map[status]}>
      {t(`statuses.${status}`)}
    </Badge>
  );
}

function EmailTimeline({ events, loading }: { events: PayrollEmailEvent[]; loading: boolean }) {
  const { t } = useTranslation();

  const fmt = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });

  // Pick the most relevant "sent" event: latest non-pending status, else latest pending.
  const sortedAsc = [...events].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
  const queuedEvent = sortedAsc.find((e) => e.status === "pending") ?? null;
  const finalSendEvent = [...sortedAsc].reverse().find((e) => e.status !== "pending") ?? null;

  const sendStatus = finalSendEvent?.status ?? queuedEvent?.status ?? null;
  const sendAt = finalSendEvent?.created_at ?? queuedEvent?.created_at ?? null;
  const sendError = finalSendEvent?.error_message ?? null;

  type Step = {
    key: string;
    label: string;
    state: "done" | "pending" | "failed" | "skipped";
    at: string | null;
    detail?: string;
  };

  const steps: Step[] = [];

  // 1. Sent
  if (sendStatus === "sent") {
    steps.push({
      key: "sent",
      label: t("pages.payroll.detail.timelineSent", {
        defaultValue: "Email delivered",
      }),
      state: "done",
      at: sendAt,
    });
  } else if (
    sendStatus === "failed" ||
    sendStatus === "dlq" ||
    sendStatus === "bounced" ||
    sendStatus === "complained" ||
    sendStatus === "suppressed"
  ) {
    steps.push({
      key: "sent",
      label: t("pages.payroll.detail.timelineFailed", {
        defaultValue: "Email failed",
      }),
      state: "failed",
      at: sendAt,
      detail: sendError ?? sendStatus,
    });
  } else if (sendStatus === "pending") {
    steps.push({
      key: "sent",
      label: t("pages.payroll.detail.timelineQueued", {
        defaultValue: "Email queued",
      }),
      state: "pending",
      at: sendAt,
    });
  } else {
    steps.push({
      key: "sent",
      label: t("pages.payroll.detail.timelineNotSent", {
        defaultValue: "No email sent yet",
      }),
      state: "skipped",
      at: null,
    });
  }

  // 2. Opened — not tracked by the email infrastructure
  steps.push({
    key: "opened",
    label: t("pages.payroll.detail.timelineOpened", {
      defaultValue: "Opened",
    }),
    state: "skipped",
    at: null,
    detail: t("pages.payroll.detail.timelineOpenedNotTracked", {
      defaultValue: "Open tracking is not enabled",
    }),
  });

  if (loading && events.length === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        {t("pages.payroll.detail.timelineLoading", {
          defaultValue: "Loading timeline…",
        })}
      </div>
    );
  }

  return (
    <ol className="space-y-3">
      {steps.map((step, idx) => {
        const Icon =
          step.state === "done"
            ? CheckCircle2
            : step.state === "failed"
              ? XCircle
              : step.state === "pending"
                ? Clock
                : Eye;
        const tone =
          step.state === "done"
            ? "text-primary"
            : step.state === "failed"
              ? "text-destructive"
              : step.state === "pending"
                ? "text-amber-500"
                : "text-muted-foreground";
        return (
          <li key={step.key} className="flex items-start gap-3">
            <div className="relative flex flex-col items-center">
              <div
                className={`flex h-6 w-6 items-center justify-center rounded-full border ${
                  step.state === "done"
                    ? "border-primary/40 bg-primary/10"
                    : step.state === "failed"
                      ? "border-destructive/40 bg-destructive/10"
                      : step.state === "pending"
                        ? "border-amber-500/40 bg-amber-500/10"
                        : "border-muted-foreground/30 bg-muted"
                }`}
              >
                <Icon className={`h-3 w-3 ${tone}`} />
              </div>
              {idx < steps.length - 1 && <span className="mt-1 h-6 w-px bg-border" />}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium leading-tight">{step.label}</p>
              {step.at && <p className="text-xs text-muted-foreground">{fmt(step.at)}</p>}
              {step.detail && (
                <p className="mt-0.5 flex items-start gap-1 text-xs text-muted-foreground">
                  <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
                  <span>{step.detail}</span>
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
